/**
 * Service layer — Members domain
 *
 * Wraps the Airtable Members table (tblzax2FPEul41fjN).
 * Callers never call /api/airtable directly — all access goes through here.
 * When Members migrates to Supabase, only this file changes.
 */

import { apiGet } from './_api.js'

const TBL_ROLES   = 'tblq1neInwg9utMpU'   // Practice Roles — the firm's real role structure
const TBL_CONTACTS = 'tblEcywoAVDwkOLiK'   // Contacts (a member's Practice Roles live here)
const TBL_MEMBERS = 'tblzax2FPEul41fjN'
const TBL_POSITIONS = 'tbleWeJskivhCnJ2j'  // Practice Positions — a person's level: pay, experience, bill rate

/** Internal Cortex staff Member Types — excludes clients, consultants, agencies, and any
 *  other external contact. Mirrors the set used to gate the time roster (see
 *  api/tenancy/provision.js's MEMBER_TYPE_BY_SEAT) plus Bookkeeper. */
export const TEAM_MEMBER_TYPES = ['Team - Principal', 'Team - Manager', 'Team - Approved', 'Team - Bookkeeper']

/**
 * Fetch all internal team members (excludes clients/consultants/agencies) for a
 * delegate picker. Returns { id, name } pairs sorted by name.
 * @returns {Promise<Array<{id: string, name: string}>>}
 */
export function getTeamMembers() {
  const formula = 'OR(' + TEAM_MEMBER_TYPES.map(t => `{Member Type}='${t}'`).join(',') + ')'
  return apiGet(
    TBL_MEMBERS,
    'filterByFormula=' + encodeURIComponent(formula) +
    '&sort%5B0%5D%5Bfield%5D=Member+Name&sort%5B0%5D%5Bdirection%5D=asc&maxRecords=200'
  ).then(records => records.map(r => ({ id: r.id, name: r.fields['Member Name'] || '' })))
}

/**
 * People a request can be handed to: the team minus the finance seats (Member Type
 * 'Team - Bookkeeper' — the accounting accounts and bookkeepers, who never review, direct or chase
 * project work). Retired people are dropped by the caller (crew roster). Returns { id, name }.
 */
export function getHandoffMembers() {
  const types = TEAM_MEMBER_TYPES.filter(t => t !== 'Team - Bookkeeper')
  const formula = 'OR(' + types.map(t => `{Member Type}='${t}'`).join(',') + ')'
  return apiGet(
    TBL_MEMBERS,
    'filterByFormula=' + encodeURIComponent(formula) +
    '&sort%5B0%5D%5Bfield%5D=Member+Name&sort%5B0%5D%5Bdirection%5D=asc&maxRecords=200'
  ).then(records => records.map(r => ({ id: r.id, name: r.fields['Member Name'] || '' })))
}

/**
 * The same roster, plus each member's Practice Role(s) — the input `outline/eligibility.js`
 * needs to say anything about skill fit (Priority Engine Phase 4, docs/PRIORITY_ENGINE.md §13).
 *
 * The role lives on the CONTACT, not the Member, and as a link field that returns record ids —
 * so this resolves Members → Contacts → Practice Roles. Shaped as `{ id, name, capacity: { practiceRoles } }` because that is exactly
 * what `personSkills()` reads (`person.capacity.practiceRoles`), so no adapter is needed.
 *
 * FAILS SOFT: any failure resolving roles returns the plain roster with `practiceRoles: null`.
 * A crew picker that cannot rank is still a working crew picker; one that throws is not.
 */
export async function getTeamMembersWithRoles() {
  const base = await getTeamMembers()
  try {
    const [roleRecs, contactRecs] = await Promise.all([
      apiGet(TBL_ROLES, 'maxRecords=200'),
      apiGet(TBL_CONTACTS, 'filterByFormula=' + encodeURIComponent("{Contact Type}='Team'") + '&maxRecords=200'),
    ])
    const roleNameById = {}
    for (const r of (roleRecs || [])) if ((r.fields || {})['Name']) roleNameById[r.id] = r.fields['Name']

    // Contact → Member is the `Record ID (from Members)` lookup, NOT a field called "Members"
    // (there isn't one) — verified against live data: all 9 Team contacts carry it, 7 carry a Practice Role.
    const rolesByMember = {}
    for (const c of (contactRecs || [])) {
      const f = c.fields || {}
      const raw = f['Record ID (from Members)']
      const mid = String((Array.isArray(raw) ? raw[0] : raw) || '').split(',')[0]
      if (!mid) continue
      const ids = Array.isArray(f['Practice Roles']) ? f['Practice Roles'] : (f['Practice Roles'] ? [f['Practice Roles']] : [])
      const names = ids.map(id => roleNameById[id]).filter(Boolean)
      const resolved = names.length ? names.join(', ')
        : (Array.isArray(f['Alder James Role']) ? f['Alder James Role'][0] : f['Alder James Role']) || null
      if (resolved) rolesByMember[mid] = resolved
    }
    return base.map(m => ({ ...m, capacity: { practiceRoles: rolesByMember[m.id] || null } }))
  } catch (_) {
    return base.map(m => ({ ...m, capacity: { practiceRoles: null } }))
  }
}

/**
 * Each team member's POSITION (Matt, 2026-10-21): their level on the pay scale (Practice Positions,
 * linked from their Contact), as distinct from the ROLES they hold on the Org Chart. One per person.
 * Resolves to { [memberId]: { name, billRate } }; {} if Airtable can't be read.
 */
export async function getTeamPositions() {
  try {
    const [posRecs, contactRecs] = await Promise.all([
      apiGet(TBL_POSITIONS, 'maxRecords=100'),
      apiGet(TBL_CONTACTS, 'filterByFormula=' + encodeURIComponent("{Contact Type}='Team'") + '&maxRecords=200'),
    ])
    const posById = {}
    for (const p of posRecs || []) {
      const f = p.fields || {}
      const rate = Array.isArray(f['Current Bill Rate']) ? f['Current Bill Rate'][0] : f['Current Bill Rate']
      if (f['Name']) posById[p.id] = { name: f['Name'], billRate: rate || null }
    }
    const out = {}
    for (const c of contactRecs || []) {
      const f = c.fields || {}
      const raw = f['Record ID (from Members)']
      const mid = String((Array.isArray(raw) ? raw[0] : raw) || '').split(',')[0]
      const pid = Array.isArray(f['Practice Position']) ? f['Practice Position'][0] : f['Practice Position']
      if (mid && pid && posById[pid]) out[mid] = posById[pid]
    }
    return out
  } catch (_) {
    return {}
  }
}

/**
 * Resolve a Clerk user's email to their Airtable Member record.
 *
 * Returns the full { id, fields } record on success, or null if no match
 * or if the request fails. Callers should handle null gracefully — the app
 * should always be usable even if member resolution fails.
 *
 * @param {string} email — the Clerk user's primary email address
 * @returns {Promise<{id: string, fields: object} | null>}
 */
export async function resolveCurrentMember(email) {
  if (!email) return null
  const formula = encodeURIComponent(`{Member Email}="${email}"`)
  try {
    const records = await apiGet(TBL_MEMBERS, `filterByFormula=${formula}&maxRecords=1`)
    return records.length > 0 ? records[0] : null
  } catch (err) {
    console.warn('[members] resolveCurrentMember failed:', err)
    return null
  }
}


/**
 * The Contact record that stands for a team member — Threads' `Ball In Court` links to Contacts,
 * not Members. Contact → Member is the `Record ID (from Members)` lookup (see getTeamMembersWithRoles).
 * Returns the contact id, or null when the member has no Contact yet.
 */
export async function contactIdForMember(memberId) {
  if (!memberId) return null
  const recs = await apiGet(TBL_CONTACTS, 'filterByFormula=' + encodeURIComponent(`FIND('${memberId}', ARRAYJOIN({Record ID (from Members)}, ','))`) + '&maxRecords=1&' + 'fields%5B%5D=Name')
  return recs && recs[0] ? recs[0].id : null
}
